import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  LayoutDashboard, 
  Building2, 
  Wallet, 
  Shield, 
  Heart, 
  Gift, 
  GraduationCap, 
  ShoppingCart,
  Settings,
  FileCheck,
  LogOut,
  X,
  ChevronRight,
  Award,
  Home,
  Trophy
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';

const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_c9a69f8c-f09a-4a22-b109-5dc5ad9f5a15/artifacts/qtk9sn35_MEDX-AI-Welcome-Poster.pptx%20%281%29.png";

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { path: '/dashboard/banking', label: 'Banking', icon: Building2 },
  { path: '/dashboard/easy-loans', label: 'Easy Loans', icon: Wallet },
  { path: '/dashboard/insurance', label: 'Insurance', icon: Shield },
  { path: '/dashboard/medical', label: 'Medical Aid', icon: Heart },
  { path: '/dashboard/loyalty', label: 'Loyalty Rewards', icon: Award },
  { path: '/dashboard/education', label: 'Education', icon: GraduationCap },
  { path: '/dashboard/marketplace', label: 'Marketplace', icon: ShoppingCart },
  { path: '/dashboard/travel', label: 'Travel', icon: Home },
  { path: '/dashboard/betting', label: 'Betting', icon: Trophy },
  { path: '/dashboard/kyc', label: 'KYC Verification', icon: FileCheck },
  { path: '/dashboard/settings', label: 'Settings', icon: Settings },
];

export const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    return location.pathname.startsWith(item.path);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar - Dark theme */}
      <aside 
        className={`
          fixed inset-y-0 left-0 z-50 w-64 
          bg-gradient-to-b from-[#0B0B0B] to-[#1a0000]
          border-r border-white/10
          transform transition-transform duration-300
          lg:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo & Close */}
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center justify-between">
              <img src={LOGO_URL} alt="AfriPerx" className="h-9 w-auto brightness-200" />
              <button 
                onClick={onClose}
                className="lg:hidden text-gray-400 hover:text-white"
                data-testid="sidebar-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* User info */}
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-afriperx-green to-afriperx-green-dark flex items-center justify-center">
                <span className="text-white font-bold">
                  {user?.name?.charAt(0) || 'U'}
                </span>
              </div>
              <div>
                <p className="text-white font-medium">{user?.name} {user?.surname}</p>
                <p className="text-xs text-gray-500">{user?.mobile}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item);
                
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      onClose();
                    }}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200
                      ${active 
                        ? 'bg-afriperx-green text-white shadow-lg shadow-afriperx-gold/30' 
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }
                    `}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                    {active && <ChevronRight className="w-4 h-4 ml-auto" />}
                  </button>
                );
              })}
            </nav>
          </ScrollArea>

          {/* Logout */}
          <div className="p-4 border-t border-white/10">
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-gray-400 hover:text-white hover:bg-white/5"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};
